import { Fragment } from "react";
import { Container, Alert, Center, Stack, Text, Button } from "@mantine/core";
import { IconSettings } from "@tabler/icons-react";
import { useLocation } from "wouter";
import { useBrandContext } from "../Brands/brandContext";
import { useBrand } from "../Brands/useBrand";

/**
 * Shared guard wrapper: checks brand selected + setup status, renders children or alert.
 * Use requireActive=true for analytics routes that need an Active brand.
 */
export default function BrandGuard({ requireActive = false, children }) {
  const { selectedBrand } = useBrandContext();
  const brand = useBrand(selectedBrand?.id);
  const [, setLocation] = useLocation();

  if (!selectedBrand) {
    return (
      <Container>
        <Alert color="blue" mt="md">
          Please select a brand from the switcher above.
        </Alert>
      </Container>
    );
  }

  if (!brand) return null;

  if (requireActive && brand.setupStatus === "PreReview") {
    return (
      <Container>
        <Center mih={300}>
          <Stack align="center" gap="md">
            <Text size="lg" fw={500}>
              This view will be available once your brand setup is complete.
            </Text>
            <Text c="dimmed">
              Finish configuring your brand to start seeing data.
            </Text>
            <Button
              leftSection={<IconSettings size={16} />}
              onClick={() => setLocation("/settings")}
            >
              Go to Settings
            </Button>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Key on brand.id to force full remount of children when the brand changes.
  // This ensures all child state (loaded data, filters, pagination) resets cleanly.
  return (
    <Fragment key={brand.id}>
      {typeof children === "function" ? children({ brand }) : children}
    </Fragment>
  );
}
